import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Users, Trophy, Target, CheckCircle, Clock, Star,
  BookOpen, Award, ChevronRight, Play, Pause, RotateCcw,
  GraduationCap, Leaf, Camera, FileText, X, Sparkles,
  UserCheck, Send, Eye, BarChart3, Download, FileDown,
  Printer, Table
} from "lucide-react";

// Mock student data
const MOCK_STUDENTS = [
  { id: 1, name: "Alex Rivera", avatar: "🧑‍🎓", progress: 0, submissions: [], status: "not_started" },
  { id: 2, name: "Jordan Chen", avatar: "👩‍🎓", progress: 0, submissions: [], status: "not_started" },
  { id: 3, name: "Sam Johnson", avatar: "🧑‍🎓", progress: 0, submissions: [], status: "not_started" },
  { id: 4, name: "Taylor Williams", avatar: "👩‍🎓", progress: 0, submissions: [], status: "not_started" },
  { id: 5, name: "Casey Martinez", avatar: "🧑‍🎓", progress: 0, submissions: [], status: "not_started" },
];

// Demo challenge
const DEMO_CHALLENGE = {
  title: "Spring Vegetable Garden",
  description: "Plan and document a spring vegetable garden with at least 5 different crops.",
  duration: "2 weeks",
  goals: [
    { id: 1, text: "Research 5 spring vegetables suitable for your climate", points: 20 },
    { id: 2, text: "Create a garden layout diagram", points: 25 },
    { id: 3, text: "Document soil preparation steps", points: 15 },
    { id: 4, text: "Plant seeds and take photos", points: 25 },
    { id: 5, text: "Write a reflection on the experience", points: 15 },
  ],
  standards: ["NGSS-LS1", "USVI-ELA-W.4"],
  totalPoints: 100,
};

// Simulated student actions
const STUDENT_ACTIONS = [
  { studentId: 2, action: "joined", delay: 2000 },
  { studentId: 1, action: "joined", delay: 3500 },
  { studentId: 4, action: "joined", delay: 5000 },
  { studentId: 2, action: "started", delay: 7000 },
  { studentId: 3, action: "joined", delay: 8000 },
  { studentId: 1, action: "started", delay: 9000 },
  { studentId: 2, action: "completed_goal", goalId: 1, delay: 12000 },
  { studentId: 5, action: "joined", delay: 14000 },
  { studentId: 1, action: "completed_goal", goalId: 1, delay: 16000 },
  { studentId: 4, action: "started", delay: 18000 },
  { studentId: 2, action: "completed_goal", goalId: 2, delay: 20000 },
  { studentId: 3, action: "started", delay: 22000 },
  { studentId: 1, action: "completed_goal", goalId: 2, delay: 24000 },
  { studentId: 2, action: "submitted", delay: 26000 },
  { studentId: 5, action: "started", delay: 28000 },
  { studentId: 4, action: "completed_goal", goalId: 1, delay: 30000 },
  { studentId: 1, action: "completed_goal", goalId: 3, delay: 32000 },
  { studentId: 3, action: "completed_goal", goalId: 1, delay: 34000 },
  { studentId: 1, action: "submitted", delay: 38000 },
  { studentId: 4, action: "completed_goal", goalId: 2, delay: 40000 },
];

const ClassroomDemo = ({ onClose }) => {
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [students, setStudents] = useState(MOCK_STUDENTS);
  const [notifications, setNotifications] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [actionIndex, setActionIndex] = useState(0);
  const [showCertificate, setShowCertificate] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  // Timer
  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 100);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  // Process student actions
  useEffect(() => {
    if (!isRunning || actionIndex >= STUDENT_ACTIONS.length) return;

    const action = STUDENT_ACTIONS[actionIndex];
    if (elapsedTime >= action.delay) {
      processAction(action);
      setActionIndex(prev => prev + 1);
    }
  }, [isRunning, elapsedTime, actionIndex]);

  const processAction = (action) => {
    const student = students.find(s => s.id === action.studentId);
    if (!student) return;

    let notification = "";
    
    setStudents(prev => prev.map(s => {
      if (s.id !== action.studentId) return s;
      
      switch (action.action) {
        case "joined":
          notification = `${s.name} joined the challenge`;
          return { ...s, status: "joined" };
        
        case "started":
          notification = `${s.name} started working on the challenge`;
          return { ...s, status: "in_progress", progress: 10 };
        
        case "completed_goal":
          const goal = DEMO_CHALLENGE.goals.find(g => g.id === action.goalId);
          notification = `${s.name} completed: "${goal?.text}"`;
          const newProgress = Math.min(s.progress + (goal?.points || 20), 100);
          return { 
            ...s, 
            progress: newProgress,
            submissions: [...s.submissions, action.goalId]
          };
        
        case "submitted":
          notification = `🎉 ${s.name} submitted their challenge!`;
          return { ...s, status: "completed", progress: 100 };
        
        default:
          return s;
      }
    }));

    if (notification) {
      addNotification(notification);
    }
  };

  const addNotification = (text) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, text }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  };

  const startDemo = () => {
    setIsRunning(true);
    addNotification("🚀 Demo started! Watch as students join and complete the challenge.");
  };

  const pauseDemo = () => {
    setIsRunning(false);
  };

  const resetDemo = () => {
    setIsRunning(false);
    setElapsedTime(0);
    setActionIndex(0);
    setStudents(MOCK_STUDENTS);
    setNotifications([]);
    setShowCertificate(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-500";
      case "in_progress": return "bg-blue-500";
      case "joined": return "bg-amber-500";
      default: return "bg-gray-300";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed": return "Completed";
      case "in_progress": return "In Progress";
      case "joined": return "Joined";
      default: return "Not Started";
    }
  };

  const completedCount = students.filter(s => s.status === "completed").length;
  const inProgressCount = students.filter(s => s.status === "in_progress").length;
  const joinedCount = students.filter(s => s.status !== "not_started").length;

  // Generate report data
  const generateReportData = () => {
    const now = new Date();
    const totalPoints = students.reduce((sum, s) => sum + (s.progress / 100 * DEMO_CHALLENGE.totalPoints), 0);
    const avgProgress = students.reduce((sum, s) => sum + s.progress, 0) / students.length;
    
    return {
      reportDate: now.toLocaleDateString(),
      reportTime: now.toLocaleTimeString(),
      challenge: DEMO_CHALLENGE,
      classStats: {
        totalStudents: students.length,
        joined: joinedCount,
        inProgress: inProgressCount,
        completed: completedCount,
        notStarted: students.length - joinedCount,
        averageProgress: Math.round(avgProgress),
        totalPointsEarned: Math.round(totalPoints),
        completionRate: Math.round((completedCount / students.length) * 100)
      },
      students: students.map(s => ({
        name: s.name,
        status: getStatusText(s.status),
        progress: s.progress,
        pointsEarned: Math.round(s.progress / 100 * DEMO_CHALLENGE.totalPoints),
        goalsCompleted: s.submissions.length,
        goalsDetails: s.submissions.map(goalId => 
          DEMO_CHALLENGE.goals.find(g => g.id === goalId)?.text || `Goal ${goalId}`
        )
      })),
      standardsCovered: DEMO_CHALLENGE.standards
    };
  };

  // Export as CSV
  const exportAsCSV = () => {
    const data = generateReportData();
    const headers = ["Student Name", "Status", "Progress (%)", "Points Earned", "Goals Completed"];
    const rows = data.students.map(s => [
      s.name,
      s.status,
      s.progress,
      s.pointsEarned,
      s.goalsCompleted
    ]);
    
    const csvContent = [
      `ROOTSPACE Classroom Report - ${data.challenge.title}`,
      `Generated: ${data.reportDate} at ${data.reportTime}`,
      ``,
      `Class Summary`,
      `Total Students,${data.classStats.totalStudents}`,
      `Completion Rate,${data.classStats.completionRate}%`,
      `Average Progress,${data.classStats.averageProgress}%`,
      `Total Points Earned,${data.classStats.totalPointsEarned}`,
      ``,
      headers.join(","),
      ...rows.map(r => r.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rootspace-demo-report-${data.reportDate.replace(/\//g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export as JSON
  const exportAsJSON = () => {
    const data = generateReportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rootspace-demo-report-${data.reportDate.replace(/\//g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Print report
  const printReport = () => {
    const data = generateReportData();
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>ROOTSPACE Classroom Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #16a34a; border-bottom: 2px solid #16a34a; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; }
          .header-info { color: #6b7280; margin-bottom: 20px; }
          .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
          .stat-card { background: #f3f4f6; padding: 15px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 24px; font-weight: bold; color: #16a34a; }
          .stat-label { font-size: 12px; color: #6b7280; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #e5e7eb; padding: 12px; text-align: left; }
          th { background: #f9fafb; font-weight: 600; }
          .progress-bar { background: #e5e7eb; border-radius: 4px; height: 8px; }
          .progress-fill { background: #16a34a; height: 100%; border-radius: 4px; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; }
          .badge-completed { background: #dcfce7; color: #166534; }
          .badge-progress { background: #dbeafe; color: #1e40af; }
          .badge-joined { background: #fef3c7; color: #92400e; }
          .badge-notstarted { background: #f3f4f6; color: #6b7280; }
          .standards { margin-top: 20px; }
          .standard-badge { display: inline-block; background: #ede9fe; color: #5b21b6; padding: 4px 12px; border-radius: 4px; margin-right: 8px; font-size: 12px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>🌱 ROOTSPACE Classroom Report</h1>
        <div class="header-info">
          <strong>Challenge:</strong> ${data.challenge.title}<br>
          <strong>Generated:</strong> ${data.reportDate} at ${data.reportTime}<br>
          <strong>Duration:</strong> ${data.challenge.duration}
        </div>

        <h2>📊 Class Summary</h2>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">${data.classStats.totalStudents}</div>
            <div class="stat-label">Total Students</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${data.classStats.completionRate}%</div>
            <div class="stat-label">Completion Rate</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${data.classStats.averageProgress}%</div>
            <div class="stat-label">Avg Progress</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${data.classStats.totalPointsEarned}</div>
            <div class="stat-label">Points Earned</div>
          </div>
        </div>

        <h2>👨‍🎓 Student Progress</h2>
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Points</th>
              <th>Goals</th>
            </tr>
          </thead>
          <tbody>
            ${data.students.map(s => `
              <tr>
                <td><strong>${s.name}</strong></td>
                <td>
                  <span class="badge badge-${s.status.toLowerCase().replace(" ", "")}">${s.status}</span>
                </td>
                <td>
                  <div class="progress-bar">
                    <div class="progress-fill" style="width: ${s.progress}%"></div>
                  </div>
                  ${s.progress}%
                </td>
                <td>${s.pointsEarned} / ${data.challenge.totalPoints}</td>
                <td>${s.goalsCompleted} / ${data.challenge.goals.length}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="standards">
          <h2>📚 Standards Addressed</h2>
          ${data.standardsCovered.map(s => `<span class="standard-badge">${s}</span>`).join("")}
        </div>

        <div class="footer">
          <p>This is a sample report from ROOTSPACE Classroom Demo Mode.</p>
          <p>Create real challenges at <strong>rootspace.pro/teacher</strong></p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <GraduationCap className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Classroom Demo Mode</h2>
              <p className="text-white/80 text-sm">Experience how students interact with challenges</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-white/20 text-white border-0">
              <Clock className="w-4 h-4 mr-1" />
              {Math.floor(elapsedTime / 1000)}s
            </Badge>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Challenge Info */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <Badge className="w-fit mb-2 bg-green-100 text-green-700">Demo Challenge</Badge>
                  <CardTitle className="text-lg">{DEMO_CHALLENGE.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{DEMO_CHALLENGE.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      {DEMO_CHALLENGE.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-500" />
                      {DEMO_CHALLENGE.totalPoints} pts
                    </span>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Goals:</p>
                    <ul className="space-y-2">
                      {DEMO_CHALLENGE.goals.map((goal, i) => (
                        <li key={goal.id} className="flex items-start gap-2 text-sm">
                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span className="text-muted-foreground">{goal.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {DEMO_CHALLENGE.standards.map(std => (
                      <Badge key={std} variant="outline" className="text-xs">
                        {std}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Demo Controls */}
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3">Demo Controls</p>
                  <div className="flex gap-2">
                    {!isRunning ? (
                      <Button onClick={startDemo} className="flex-1 bg-green-600 hover:bg-green-700">
                        <Play className="w-4 h-4 mr-2" />
                        {elapsedTime > 0 ? "Resume" : "Start Demo"}
                      </Button>
                    ) : (
                      <Button onClick={pauseDemo} variant="outline" className="flex-1">
                        <Pause className="w-4 h-4 mr-2" />
                        Pause
                      </Button>
                    )}
                    <Button onClick={resetDemo} variant="outline" size="icon">
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Watch simulated students join, work on goals, and submit their challenges in real-time.
                  </p>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3">Class Overview</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{joinedCount}</p>
                      <p className="text-xs text-muted-foreground">Joined</p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{inProgressCount}</p>
                      <p className="text-xs text-muted-foreground">Working</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{completedCount}</p>
                      <p className="text-xs text-muted-foreground">Done</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Student Progress */}
            <div className="lg:col-span-2 space-y-4">
              {/* Notifications */}
              <div className="h-24 relative overflow-hidden bg-gray-50 rounded-lg border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">Live Activity Feed</p>
                <AnimatePresence>
                  {notifications.slice(-3).map((notif, i) => (
                    <motion.div
                      key={notif.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="text-sm py-1"
                    >
                      {notif.text}
                    </motion.div>
                  ))}
                </AnimatePresence>
                {notifications.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Click &quot;Start Demo&quot; to see student activity...
                  </p>
                )}
              </div>

              {/* Student Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {students.map(student => (
                  <motion.div
                    key={student.id}
                    layout
                    className="relative"
                  >
                    <Card className={`transition-all ${student.status === "completed" ? "ring-2 ring-green-500" : ""}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-3xl">{student.avatar}</div>
                          <div className="flex-1">
                            <p className="font-medium">{student.name}</p>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${getStatusColor(student.status)}`} />
                              <span className="text-xs text-muted-foreground">
                                {getStatusText(student.status)}
                              </span>
                            </div>
                          </div>
                          {student.status === "completed" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowCertificate(student)}
                              className="text-xs"
                            >
                              <Award className="w-3 h-3 mr-1" />
                              Certificate
                            </Button>
                          )}
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-medium">{student.progress}%</span>
                          </div>
                          <Progress value={student.progress} className="h-2" />
                        </div>

                        {student.submissions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {student.submissions.map(goalId => (
                              <Badge key={goalId} variant="secondary" className="text-xs">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Goal {goalId}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Teacher Actions Preview */}
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Teacher Actions
                  </p>
                  <div className="grid sm:grid-cols-4 gap-2">
                    <Button variant="outline" size="sm" className="text-xs" disabled={completedCount === 0}>
                      <Award className="w-3 h-3 mr-1" />
                      Award Certificates
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Eye className="w-3 h-3 mr-1" />
                      View Submissions
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs bg-purple-100 hover:bg-purple-200 border-purple-300"
                      onClick={() => setShowReportModal(true)}
                    >
                      <BarChart3 className="w-3 h-3 mr-1" />
                      Export Report
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Send className="w-3 h-3 mr-1" />
                      Send Feedback
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            This is a simulation. Create a real challenge to use with your students.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Close Demo
            </Button>
            <Button 
              onClick={() => {
                onClose();
                navigate("/teacher");
              }}
              className="bg-gradient-to-r from-amber-500 to-orange-500"
            >
              Go to Teacher Dashboard
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Certificate Preview Modal */}
        <AnimatePresence>
          {showCertificate && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 flex items-center justify-center p-4"
              onClick={() => setShowCertificate(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-xl p-8 max-w-md w-full text-center"
                onClick={e => e.stopPropagation()}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Certificate of Completion</h3>
                <p className="text-muted-foreground mb-4">This certifies that</p>
                <p className="text-xl font-bold text-primary mb-4">{showCertificate.name}</p>
                <p className="text-muted-foreground mb-4">
                  has successfully completed the challenge
                </p>
                <p className="font-medium mb-6">&quot;{DEMO_CHALLENGE.title}&quot;</p>
                <Badge className="bg-green-100 text-green-700 mb-6">
                  <Star className="w-4 h-4 mr-1" />
                  {DEMO_CHALLENGE.totalPoints} Points Earned
                </Badge>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => setShowCertificate(null)}>
                    Close
                  </Button>
                  <Button>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Export Report Modal */}
        <AnimatePresence>
          {showReportModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowReportModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      <h3 className="font-bold text-lg">Export Demo Report</h3>
                    </div>
                    <button
                      onClick={() => setShowReportModal(false)}
                      className="p-1 hover:bg-white/20 rounded"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-white/80 text-sm mt-1">
                    Download a sample report showing class progress
                  </p>
                </div>

                {/* Report Preview */}
                <div className="p-4 border-b">
                  <p className="text-sm font-medium mb-3">Report Preview</p>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <p className="text-xl font-bold text-gray-900">{students.length}</p>
                      <p className="text-xs text-muted-foreground">Students</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded-lg">
                      <p className="text-xl font-bold text-green-600">{completedCount}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <p className="text-xl font-bold text-blue-600">
                        {Math.round(students.reduce((sum, s) => sum + s.progress, 0) / students.length)}%
                      </p>
                      <p className="text-xs text-muted-foreground">Avg Progress</p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <p className="text-xl font-bold text-amber-600">
                        {Math.round(students.reduce((sum, s) => sum + (s.progress / 100 * DEMO_CHALLENGE.totalPoints), 0))}
                      </p>
                      <p className="text-xs text-muted-foreground">Points</p>
                    </div>
                  </div>
                </div>

                {/* Export Options */}
                <div className="p-4 space-y-3">
                  <p className="text-sm font-medium mb-2">Choose Export Format</p>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-3"
                    onClick={() => {
                      printReport();
                      setShowReportModal(false);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <Printer className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Print / PDF</p>
                        <p className="text-xs text-muted-foreground">Beautiful formatted report ready to print</p>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-3"
                    onClick={() => {
                      exportAsCSV();
                      setShowReportModal(false);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Table className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">CSV Spreadsheet</p>
                        <p className="text-xs text-muted-foreground">Open in Excel, Google Sheets, or Numbers</p>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-3"
                    onClick={() => {
                      exportAsJSON();
                      setShowReportModal(false);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileDown className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">JSON Data</p>
                        <p className="text-xs text-muted-foreground">Raw data for integration with other tools</p>
                      </div>
                    </div>
                  </Button>
                </div>

                {/* Footer */}
                <div className="p-4 bg-gray-50 border-t">
                  <p className="text-xs text-muted-foreground text-center">
                    This is a sample report from demo data. Real reports include more detailed analytics.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default ClassroomDemo;
